import type { IntlShape } from 'react-intl';

import { BulkActionType } from '~/enums/BulkActionType';
import { DataFieldType } from '~/enums/DataFieldType';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';

export const convertFieldValueData = (
  type: DataFieldType,
  value: any,
  formatDate: useFormatDateReturnType,
) => {
  if (type === DataFieldType.DATE) {
    return value ? formatDate(value as Date, 'iso') : null;
  }
  return value;
};

export const validateFieldValue = (type: DataFieldType, value: any) => {
  if (value === undefined || value === null) return false;
  switch (type) {
    case DataFieldType.ARRAY_MULTIPLE:
      return Array.isArray(value) && value.length > 0;
    case DataFieldType.ARRAY_SINGLE:
      return value?.length > 0;
    case DataFieldType.ATTACHMENT:
      return true;
    case DataFieldType.BOOLEAN:
      return value === true || value === false;
    case DataFieldType.DATE:
    case DataFieldType.NUMBER:
    case DataFieldType.TEXT_AREA:
    case DataFieldType.STRING:
      return !!value;
    default:
      return !!value;
  }
};

export const getPanelText = (
  intl: IntlShape,
  activePanel: BulkActionType,
  selectedCount: number,
) => {
  switch (activePanel) {
    case 'copy':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.copyPanelText',
          defaultMessage:
            'Choose a folder to copy the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the copy panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'move':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.movePanelText',
          defaultMessage:
            'Choose a folder to move the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the move panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'delete':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.deletePanelText',
          defaultMessage:
            'Choose a folder to delete the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the delete panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'edit':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.editPanelText',
          defaultMessage:
            'Choose a folder to edit the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the edit panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'edit-clauses':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.editClausesPanelText',
          defaultMessage:
            'Choose a folder to edit clauses for the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the edit clauses panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'export':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.exportPanelText',
          defaultMessage:
            'Choose a folder to export the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the export panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'add-to-group':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.addToGroupPanelText',
          defaultMessage:
            'Choose a folder to add to group the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the add to group panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'permanentlyDelete':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.permanentlyDeletePanelText',
          defaultMessage:
            'Choose a folder to permanently delete the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the permanently delete panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    case 'restore':
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.restorePanelText',
          defaultMessage:
            'Choose a folder to restore the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Text shown in the restore panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
    default:
      // Fallback for any other action types
      return intl.formatMessage(
        {
          id: 'MoveCopyActionPanel.defaultPanelText',
          defaultMessage:
            'Choose a folder for the **{selectedCount}** {selectedCount, plural, one {selected document} other {selected documents}} and {selectedCount, plural, one {its} other {their}} duplicates',
          description:
            'Default text shown in the panel with pluralization. Example: selectedCount = 5',
        },
        {
          selectedCount,
        },
      );
  }
};
